import React, { useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { EditOutlined } from "@ant-design/icons";

import { controller as rootController } from "../../../../controller";

/**
 * The uploader's note on an estimate, shown directly above the document.
 *
 * Placed before the preview rather than after it because it is the thing that
 * explains the document — "the carrier wants the pre-auth number first" is what
 * someone opening the estimate needs before they start reading a PDF, not after.
 *
 * Renders as an editor when there is no note yet, so an estimate uploaded before
 * this existed (or filed in a hurry) can still be annotated. That is also why the
 * empty state is a quiet "Add a note" affordance and not a blank box: on a list
 * of estimates it should invite rather than nag.
 */
export default function EstimateNoteBlock({ caseId, note, author, updatedAt, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note || "");
  const [saving, setSaving] = useState(false);

  // Re-sync when the user selects a different estimate — this component stays
  // mounted across selections, so without this the previous note's text would
  // follow them to the next one.
  useEffect(
    function () {
      setDraft(note || "");
      setEditing(false);
    },
    [caseId, note]
  );

  async function handleSave() {
    setSaving(true);
    try {
      const res = await rootController.updatePredeterminationCaseNotes(caseId, draft);
      if (res && res.status < 300) {
        message.success(draft.trim() ? "Note saved." : "Note removed.");
        setEditing(false);
        if (onSaved) onSaved();
      } else {
        message.error(((res && res.json) || {}).detail || "Couldn't save the note.");
      }
    } catch (e) {
      message.error("Couldn't save the note.");
    } finally {
      setSaving(false);
    }
  }

  function byline() {
    const parts = [];
    if (author) parts.push("Added by " + author);
    if (updatedAt) {
      const d = new Date(updatedAt);
      if (!isNaN(d.getTime())) {
        parts.push(
          d.toLocaleString("en-US", {
            month: "short", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
          })
        );
      }
    }
    return parts.join(" · ");
  }

  if (editing) {
    return (
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: "#3d2f5e", margin: "0 0 8px 0" }}>Note</p>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 10 }}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What the carrier said, what is still outstanding…"
        />
        <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
          <Button
            size="small"
            disabled={saving}
            onClick={() => { setDraft(note || ""); setEditing(false); }}
          >
            Cancel
          </Button>
          <Button size="small" type="primary" loading={saving} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>
          Add a note
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: 16,
        border: "1px solid #e8e0f5",
        borderRadius: 8,
        background: "#faf8ff",
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: "#3d2f5e", margin: 0 }}>Note</p>
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditing(true)}>
          Edit
        </Button>
      </div>
      {/* preserve the author's line breaks — notes are written as short
          paragraphs and collapsing them makes a phone log unreadable */}
      <div style={{ whiteSpace: "pre-wrap", color: "#3d2f5e", fontSize: 13, marginTop: 6 }}>
        {note}
      </div>
      {byline() ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "#8b7bb0" }}>{byline()}</div>
      ) : null}
    </div>
  );
}
